'use client';

import { Check, Plus } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { capitalize } from '@/shared/lib/text';
import { cn } from '@/shared/lib/cn';
import type { Project } from '@/types/project';

interface ProjectListMenuProps {
  projects: Project[];
  currentProjectId?: string;
  onSelect: (projectId: string) => void;
  onCreate: () => void;
}

/**
 * Shared popover body listing the user's buildings plus a "create building"
 * action. Used by the sidebar project switcher, the mobile bottom-bar switcher
 * and the project-header pill so the three stay in lockstep (DRY).
 */
export function ProjectListMenu({
  projects,
  currentProjectId,
  onSelect,
  onCreate,
}: ProjectListMenuProps) {
  const t = useTranslations('dashboard');

  return (
    <div>
      <div className="max-h-60 overflow-y-auto">
        {projects.map((project) => (
          <button
            key={project.id}
            onClick={() => onSelect(project.id)}
            className={cn(
              'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-list-item-subtitle hover:bg-muted/50 transition-colors',
              project.id === currentProjectId && 'bg-muted/30'
            )}
          >
            {project.id === currentProjectId ? (
              <Check className="h-4 w-4 text-primary shrink-0" />
            ) : (
              <span className="w-4 shrink-0" />
            )}
            <span className="truncate">
              {capitalize(project.attributes.name)}
            </span>
          </button>
        ))}
      </div>
      <div className="my-1 h-px bg-border" />
      <button
        onClick={onCreate}
        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-list-item-subtitle text-primary hover:bg-muted/50 transition-colors"
      >
        <Plus className="h-4 w-4 shrink-0" />
        <span className="truncate">{t('newProject.buttonLabel')}</span>
      </button>
    </div>
  );
}
